import { debounce } from 'lodash';
import { Observable, Subscription, timer, take } from 'rxjs';
import { WebNativeStorage } from './sync/web-native-storage';

export class LazyStorage<T> {
    private static readonly storage = new WebNativeStorage('lazy-storage');
    private static write$?: Observable<any>;
    private writeSubscription?: Subscription;
    private cache?: { [key: string]: T };
    /** Counts clears, so a set still waiting on the first read when a clear lands is dropped with everything else. */
    private clears = 0;
    reset = debounce(() => {
        this.cache = undefined;
    }, 15_000);

    constructor(public key: string) {
    }

    /** Empties the map, in memory at once so a write still pending cannot bring back what was cleared. */
    clear() {
        this.clears++;
        this.cache = {};
        LazyStorage.storage.setMap(this.key, {});
    }

    /** The map, read from storage once; a read that finishes after a `clear()` or another read keeps what is cached. */
    async map() {
        if (!this.cache) {
            const data = await LazyStorage.storage.getMap(this.key);
            this.cache ??= (data || {}) as { [key: string]: T };
        }
        this.reset();
        return this.cache;
    }

    async get(key: string) {
        return (await this.map())[key];
    }

    async set(key: string, value: T) {
        const clears = this.clears;
        const map = await this.map();
        if (clears !== this.clears) return;
        map[key] = value;
        this.write();
    }

    async remove(key: string) {
        delete (await this.map())[key];
        this.write();
    }

    // Ensures all lazy storage gets flushed in the order they were written to avoid data inconsistencies
    private write() {
        if (!this.writeSubscription) {
            if (!LazyStorage.write$) {
                LazyStorage.write$ = timer(250).pipe(take(1));
                LazyStorage.write$.subscribe(() => (LazyStorage.write$ = undefined));
            }
            this.writeSubscription = LazyStorage.write$.subscribe(async () => {
                this.writeSubscription = undefined;

                LazyStorage.storage.setMap(this.key, await this.map());
            });
        }
    }
}
